/**
 * Product page: option selection, live price and add label, add to bag, the mobile sticky bar and
 * the click-and-hold loupe on the macro shot. Loaded by sections/main-product.liquid.
 * Pure helpers are exported for tests; see docs/contracts.md for the cart events.
 */
import { addToCart, defineElement, prefersReducedMotion } from '@wispaire/global';

const BaseElement = globalThis.HTMLElement ?? class {};

/** The variant whose options equal the selection, or undefined when that combination does not exist. */
export function findVariant(variants, options) {
  return variants.find((variant) => variant.options.every((value, index) => value === options[index]));
}

/**
 * What the purchase module shows for a variant: formatted price, add label, whether it can be added.
 * `labels` are the page's own words (data-label-* on <product-purchase>, from the locale).
 */
export function variantView(variant, labels) {
  if (!variant) return { price: '', add: labels.unavailable, available: false };
  return {
    price: variant.price_label,
    add: variant.available ? labels.add.replace('[price]', variant.price_label) : labels.soldOut,
    available: variant.available,
  };
}

/** The sticky bar shows once the main add button has scrolled up out of view, never before it. */
export function stickyVisible(entry) {
  return !entry.isIntersecting && entry.boundingClientRect.top < 0;
}

/**
 * Whether a submit of the product form means "add to bag": the main add button, or Enter in the
 * form (no submitter). App blocks render inside the form, so their untyped buttons submit it too.
 */
export function addsToBag(event, mainButton) {
  return !event.submitter || event.submitter === mainButton;
}

/** Adds one of a variant to the bag; global.js fires `cart:added`, which opens the drawer. */
export function addVariant(variant, options = {}) {
  return addToCart([{ id: variant.id, quantity: 1 }], options);
}

/** Zoom origin for the loupe, as percentages of the image box, clamped to its edges. */
export function loupeOrigin(point, rect) {
  const clamp = (value) => Math.min(100, Math.max(0, value));
  return {
    x: clamp(((point.clientX - rect.left) / rect.width) * 100),
    y: clamp(((point.clientY - rect.top) / rect.height) * 100),
  };
}

/** Where a thumbnail rail scrolls to so `thumb` sits centred in it (offsets from the rail). */
export function railScroll(thumb, rail) {
  return {
    left: Math.max(0, thumb.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2),
    top: Math.max(0, thumb.offsetTop - (rail.clientHeight - thumb.offsetHeight) / 2),
  };
}

/**
 * <product-purchase> owns the selection. It fires `product:change` (detail: { variant, size })
 * so the size guide drawer can follow, and exposes selectSize() for it.
 */
export class ProductPurchase extends BaseElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.sizeIndex = Number(this.dataset.sizeIndex ?? -1);
    this.form = this.querySelector('form');
    this.mainButton = this.form.querySelector('[data-add]');
    this.idInput = this.form.querySelector('input[name="id"]');

    this.form.addEventListener('change', () => this.update());
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (addsToBag(event, this.mainButton)) this.add(this.mainButton);
    });
    this.querySelector('[data-sticky-add]')?.addEventListener('click', (event) => this.add(event.currentTarget));
    this.watchMainButton();
    this.update({ silent: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  selectedOptions() {
    return [...this.form.querySelectorAll('[data-option]')].map(
      (group) => group.querySelector('input:checked')?.value ?? group.dataset.value,
    );
  }

  get size() {
    return this.sizeIndex >= 0 ? this.selectedOptions()[this.sizeIndex] : null;
  }

  selectSize(value) {
    const group = this.form.querySelectorAll('[data-option]')[this.sizeIndex];
    const input = [...(group?.querySelectorAll('input') ?? [])].find((radio) => radio.value === value);
    if (input && !input.checked) {
      input.checked = true;
      this.update();
    }
  }

  update({ silent = false } = {}) {
    this.variant = findVariant(this.variants, this.selectedOptions());
    const { labelAdd: add, labelSoldOut: soldOut, labelUnavailable: unavailable } = this.dataset;
    const view = variantView(this.variant, { add, soldOut, unavailable });
    for (const node of this.querySelectorAll('[data-price]')) node.textContent = view.price;
    for (const node of this.querySelectorAll('[data-add-label]')) node.textContent = view.add;
    // The size radios carry their label (S, M, L or 60×80") from Liquid.
    const sizeGroup = this.form.querySelectorAll('[data-option]')[this.sizeIndex];
    const sizeLabel = sizeGroup?.querySelector('input:checked')?.dataset.label ?? '';
    for (const node of this.querySelectorAll('[data-size-label]')) node.textContent = sizeLabel;
    for (const button of this.querySelectorAll('[data-add], [data-sticky-add]')) button.disabled = !view.available;
    for (const node of this.querySelectorAll('[data-express]')) node.hidden = !view.available;
    this.setError('');
    // No such combination: the id is left out of the form, so neither the add button nor
    // Buy it now can send the variant that was showing before.
    this.idInput.disabled = !this.variant;
    if (this.variant) {
      this.idInput.value = this.variant.id;
      if (!silent) {
        const url = new URL(globalThis.location.href);
        url.searchParams.set('variant', this.variant.id);
        globalThis.history.replaceState(globalThis.history.state, '', url);
      }
    }
    this.dispatchEvent(new CustomEvent('product:change', { bubbles: true, detail: { variant: this.variant, size: this.size } }));
  }

  /** Adds the selected variant. Busy state and errors stay on the button that asked. */
  async add(button) {
    if (!this.variant?.available || this.busy) return;
    this.busy = true;
    button?.setAttribute('aria-busy', 'true');
    try {
      await addVariant(this.variant, { source: this });
    } catch (error) {
      this.setError(error.message);
    } finally {
      this.busy = false;
      button?.removeAttribute('aria-busy');
    }
  }

  setError(message) {
    const node = this.querySelector('[data-error]');
    if (node) node.textContent = message;
  }

  watchMainButton() {
    const bar = this.querySelector('.sticky-add');
    if (!bar || !globalThis.IntersectionObserver) return;
    this.observer = new IntersectionObserver(([entry]) => {
      const visible = stickyVisible(entry);
      bar.toggleAttribute('data-visible', visible);
      bar.inert = !visible;
    });
    this.observer.observe(this.mainButton);
  }
}

/**
 * <product-loupe> magnifies the macro shot while pressed: zoom starts on pointer-down at the
 * pressed point and follows the pointer until release. Space or Enter toggles a centred zoom.
 */
class ProductLoupe extends BaseElement {
  connectedCallback() {
    this.image = this.querySelector('img');
    this.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      this.setPointerCapture(event.pointerId);
      this.zoom(event);
    });
    this.addEventListener('pointermove', (event) => {
      if (this.hasAttribute('data-zoomed')) this.moveTo(event);
    });
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) this.addEventListener(type, () => this.release());
    this.addEventListener('keydown', (event) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      if (this.hasAttribute('data-zoomed')) this.release();
      else this.zoom(null);
    });
    this.addEventListener('blur', () => this.release());
  }

  zoom(event) {
    // Load the large rendition only when someone asks for detail.
    if (this.dataset.zoomSrc && this.image.src !== this.dataset.zoomSrc) {
      this.image.srcset = '';
      this.image.src = this.dataset.zoomSrc;
    }
    if (event) {
      this.moveTo(event);
    } else {
      this.style.setProperty('--loupe-x', '50%');
      this.style.setProperty('--loupe-y', '50%');
    }
    this.setAttribute('data-zoomed', '');
    this.setAttribute('aria-pressed', 'true');
  }

  moveTo(event) {
    const { x, y } = loupeOrigin(event, this.getBoundingClientRect());
    this.style.setProperty('--loupe-x', `${x}%`);
    this.style.setProperty('--loupe-y', `${y}%`);
  }

  release() {
    this.removeAttribute('data-zoomed');
    this.setAttribute('aria-pressed', 'false');
  }
}

/**
 * Gallery thumbnails scroll their slide into view and track which slide is showing. On wide
 * screens the gallery also records how far down the page it starts (the header's real height) as
 * --gallery-top on the product composition, where the desktop sizes are computed, so the slide
 * fits the viewport under a header of any height. It measures again whenever the header group
 * changes size (a font swap, a dismissed announcement), not on every window resize.
 */
export class ProductGallery extends BaseElement {
  connectedCallback() {
    this.wide = matchMedia('(min-width: 990px)');
    this.measure = () => {
      if (this.wide.matches) this.parentElement.style.setProperty('--gallery-top', `${this.getBoundingClientRect().top + globalThis.scrollY}px`);
    };
    this.headerSize = new ResizeObserver(this.measure);
    for (const part of document.querySelectorAll('.shopify-section-group-header-group')) this.headerSize.observe(part);
    this.measure();
    this.wide.addEventListener('change', this.measure);
    this.track = this.querySelector('[data-track]');
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.current = 0;
    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => {
        const slide = this.track.children[Number(thumb.dataset.thumb)];
        slide.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      });
    }
    if (!globalThis.IntersectionObserver) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = [...this.track.children].indexOf(entry.target);
          this.show(index);
          this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
          // Swiped to a slide whose thumb is out of the rail's view: bring it in, not the page.
          const thumb = this.thumbs[index];
          if (thumb) thumb.parentElement.scrollTo({ ...railScroll(thumb, thumb.parentElement), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
      },
      { root: this.track, threshold: 0.6 },
    );
    for (const slide of this.track.children) this.observer.observe(slide);
  }

  /** Records the slide now showing; a different one than before fires `gallery:change` (detail: { index, kind }). */
  show(index) {
    if (index === this.current) return;
    this.current = index;
    const kind = this.track.children[index]?.dataset.kind;
    this.dispatchEvent(new CustomEvent('gallery:change', { bubbles: true, detail: { index, kind } }));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.headerSize?.disconnect();
    this.wide?.removeEventListener('change', this.measure);
  }
}

defineElement('product-purchase', ProductPurchase);
defineElement('product-loupe', ProductLoupe);
defineElement('product-gallery', ProductGallery);
